import React from "react";
import Seo from "../components/Seo";
import { Section, Eyebrow, PageHero } from "../components/primitives";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTABanner from "../components/CTABanner";
import EagleBackdrop from "../components/EagleBackdrop";
import { Target, Eye, Dumbbell, Apple, Crown, HeartPulse } from "lucide-react";

const pillars = [
  { icon: Dumbbell, t: "Training", d: "Personalized coaching and workout tracking that adapt to your goals and progress." },
  { icon: Apple, t: "Nutrition", d: "Food logging, macros, hydration and meal planning connected to how you perform." },
  { icon: Crown, t: "The Kingdom experience", d: "Your Kingdom Score and Kingdom Insights bring everything into one clear view." },
  { icon: HeartPulse, t: "Connected health", d: "Supported devices and platforms sync so your health data works together." },
];

export default function About() {
  return (
    <div>
      <Seo
        title="About"
        path="/about"
        description="The story, mission and vision behind Jollof Fit Kingdom — a complete fitness ecosystem built around training, nutrition, coaching, progress and connected health."
        noindex
      />
      <PageHero
        eyebrow="About Jollof Fit Kingdom"
        title="Building the future of"
        highlight="connected fitness"
        description="Jollof Fit Kingdom was built on a simple belief: your health and fitness data should work together. We're building the complete ecosystem that makes it happen."
      />

      <Section className="mt-8">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
              Why we exist
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-zinc-400">
              <p>
                Fitness technology has become fragmented. Your workouts live in one app, your nutrition in another,
                your body measurements on a scale that barely talks to anything, and your health data scattered across
                devices that don't understand each other.
              </p>
              <p>
                Jollof Fit Kingdom exists to change that. We bring training, nutrition, connected health, smart devices
                and personalized coaching into one complete fitness ecosystem — so every signal your body produces
                works together.
              </p>
              <p>
                We're not building another fitness app. We're building the complete Kingdom experience that connects
                your entire health and performance journey, for people all around the world.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-6">
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-8">
                <Target className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-xl text-white">Mission</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  To build the future of fitness by combining training, nutrition, connected health, smart devices and
                  personalized coaching into one complete ecosystem for people around the world.
                </p>
              </div>
              <div className="rounded-2xl border border-gold/20 bg-gradient-to-b from-[#12100a] to-[#0a0a0a] p-8">
                <Eye className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-xl text-white">Vision</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  To become the world's leading connected fitness ecosystem — where every workout, every meal, every
                  step, every body measurement and every health insight work together.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Eagle banner */}
      <Section className="mt-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <div className="relative h-56 md:h-72">
              <EagleBackdrop position="center" fadeBottom={false} />
              <div className="relative flex h-full flex-col items-center justify-center px-6 text-center">
                <p className="eyebrow">Our Symbol</p>
                <h2 className="mt-4 max-w-2xl font-heading text-2xl font-medium tracking-tight text-white md:text-3xl">
                  Leadership. Discipline. Focus. Excellence.
                </h2>
                <p className="mt-3 max-w-xl text-sm text-zinc-400">
                  The eagle represents the mindset behind Jollof Fit Kingdom — calm, disciplined and relentless in
                  pursuit of mastery.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* What we're building */}
      <Section className="mt-32">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>The Kingdom</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
              What we're building
            </h2>
            <p className="mt-5 text-base text-zinc-400">
              Jollof Fit Kingdom brings the core of your health and fitness journey together in one premium experience.
            </p>
          </Reveal>
        </div>
        <Stagger className="mt-12 grid gap-6 md:grid-cols-2">
          {pillars.map((p) => (
            <StaggerItem key={p.t}>
              <div className="flex h-full gap-5 rounded-2xl border border-white/10 bg-[#0a0a0a] p-7">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gold">
                  <p.icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-white">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{p.d}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* Values */}
      <Section className="mt-32">
        <Stagger className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Crown, t: "Members first", d: "Everything we build exists to help you train, eat and recover better." },
            { icon: Target, t: "Long-term vision", d: "We build for the long term, not the next trend." },
            { icon: Eye, t: "Global by design", d: "Health technology should be accessible to people everywhere." },
          ].map((v) => (
            <StaggerItem key={v.t}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#0a0a0a] p-8">
                <v.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-lg text-white">{v.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{v.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      <CTABanner />
    </div>
  );
}
